import { motion } from "framer-motion";

// Define the possible values for activeTab as a union type
type ActiveTab = "overview" | "orders" | "addresses" | "settings";

// Skeleton StatCard Component
const SkeletonStatCard = () => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
    className="bg-gray-200 dark:bg-gray-800 rounded-xl p-3 sm:p-4 shadow-sm w-full animate-pulse"
  >
    <div className="space-y-2">
      <div className="h-3 w-16 sm:w-20 bg-gray-300 dark:bg-gray-700 rounded"></div>
      <div className="h-5 w-12 sm:w-16 bg-gray-300 dark:bg-gray-700 rounded"></div>
    </div>
  </motion.div>
);

// Skeleton UserInfoCard Component
const SkeletonUserInfoCard = () => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
    className="bg-gray-200 dark:bg-gray-800 rounded-xl p-4 sm:p-6 shadow-sm mb-4 sm:mb-6 w-full animate-pulse"
  >
    <div className="h-6 w-32 sm:w-40 bg-gray-300 dark:bg-gray-700 rounded mb-3 sm:mb-4"></div>
    <div className="space-y-2">
      {Array(5).fill(0).map((_, index) => (
        <div key={index} className="h-3 w-full sm:w-3/4 bg-gray-300 dark:bg-gray-700 rounded"></div>
      ))}
    </div>
  </motion.div>
);

// Skeleton OrderCard Component
const SkeletonOrderCard = () => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
    className="bg-gray-200 dark:bg-gray-800 rounded-xl p-3 sm:p-4 shadow-sm border border-gray-300 dark:border-gray-700 animate-pulse w-full"
  >
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <div className="h-4 w-20 sm:w-24 bg-gray-300 dark:bg-gray-700 rounded"></div>
        <div className="h-4 w-16 sm:w-20 bg-gray-300 dark:bg-gray-700 rounded-full"></div>
      </div>
      <div className="text-xs space-y-1">
        <div className="h-3 w-3/4 bg-gray-300 dark:bg-gray-700 rounded"></div>
        <div className="h-3 w-1/2 bg-gray-300 dark:bg-gray-700 rounded"></div>
      </div>
      <div className="bg-gray-300 dark:bg-gray-700 p-2 rounded-lg space-y-1">
        <div className="h-3 w-full bg-gray-400 dark:bg-gray-600 rounded"></div>
        <div className="h-3 w-3/4 bg-gray-400 dark:bg-gray-600 rounded"></div>
      </div>
      <div className="space-y-1">
        <div className="h-3 w-1/4 bg-gray-300 dark:bg-gray-700 rounded"></div>
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gray-300 dark:bg-gray-700 rounded-md"></div>
          <div className="h-3 w-1/2 bg-gray-300 dark:bg-gray-700 rounded"></div>
          <div className="h-3 w-1/4 bg-gray-300 dark:bg-gray-700 rounded ml-auto"></div>
        </div>
        <div className="h-3 w-1/3 bg-gray-300 dark:bg-gray-700 rounded"></div>
      </div>
      <div className="flex gap-2">
        <div className="h-6 w-1/2 bg-gray-300 dark:bg-gray-700 rounded-full"></div>
        <div className="h-6 w-1/2 bg-gray-300 dark:bg-gray-700 rounded-full"></div>
      </div>
      <div className="h-3 w-1/2 bg-gray-300 dark:bg-gray-700 rounded"></div>
    </div>
  </motion.div>
);

// Skeleton FilterBar Component
const SkeletonFilterBar = () => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
    className="mb-4 sm:mb-6 w-full animate-pulse"
  >
    <div className="space-y-3">
      <div className="h-8 w-full sm:w-64 bg-gray-300 dark:bg-gray-700 rounded-full"></div>
      <div className="flex flex-wrap gap-2 justify-center sm:justify-start">
        {Array(5).fill(0).map((_, index) => (
          <div key={index} className="h-6 w-16 sm:w-20 bg-gray-300 dark:bg-gray-700 rounded-full"></div>
        ))}
      </div>
    </div>
  </motion.div>
);

// Skeleton Form Component
const SkeletonForm = () => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
    className="w-full max-w-md mx-auto animate-pulse"
  >
    <div className="space-y-4">
      <div className="h-8 w-32 sm:w-40 bg-gray-300 dark:bg-gray-700 rounded mb-4"></div>
      <div className="bg-gray-300 dark:bg-gray-700 p-3 sm:p-4 rounded-xl shadow-sm">
        <div className="h-3 w-1/2 bg-gray-400 dark:bg-gray-600 rounded mb-2"></div>
        <div className="h-4 w-full bg-gray-400 dark:bg-gray-600 rounded"></div>
      </div>
      <div className="space-y-2">
        <div className="h-3 w-1/4 bg-gray-300 dark:bg-gray-700 rounded"></div>
        <div className="h-16 w-full bg-gray-300 dark:bg-gray-700 rounded-xl"></div>
      </div>
      <div className="h-10 w-full sm:w-32 bg-gray-300 dark:bg-gray-700 rounded-full"></div>
    </div>
  </motion.div>
);

// Skeleton Sidebar Component
const SkeletonSidebar = () => (
  <motion.div
    initial={{ opacity: 0, x: -10 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ duration: 0.5 }}
    className="fixed inset-y-0 left-0 w-48 sm:w-56 bg-gray-200 dark:bg-gray-800 shadow-md transform lg:static lg:w-48 lg:rounded-xl lg:shadow-md animate-pulse"
  >
    <div className="p-3 sm:p-4 space-y-4 sm:space-y-6">
      <div className="flex justify-between items-center">
        <div className="h-4 w-4 bg-gray-300 dark:bg-gray-700 rounded-full lg:hidden"></div>
      </div>
      <div className="text-center space-y-2">
        <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gray-300 dark:bg-gray-700 rounded-full mx-auto"></div>
        <div className="h-4 w-24 bg-gray-300 dark:bg-gray-700 rounded mx-auto"></div>
        <div className="h-3 w-32 bg-gray-300 dark:bg-gray-700 rounded mx-auto"></div>
      </div>
      <div className="space-y-2">
        {Array(5).fill(0).map((_, index) => (
          <div key={index} className="h-8 w-full bg-gray-300 dark:bg-gray-700 rounded-full"></div>
        ))}
      </div>
    </div>
  </motion.div>
);

// Skeleton Header Component
const SkeletonHeader = () => (
  <motion.div
    initial={{ opacity: 0, y: -10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
    className="bg-gray-200 dark:bg-gray-800 shadow-md py-3 sm:py-4 px-4 sm:px-6 flex justify-between items-center sticky top-0 z-30 animate-pulse w-full"
  >
    <div className="flex items-center gap-2">
      <div className="h-5 w-5 bg-gray-300 dark:bg-gray-700 rounded-full lg:hidden"></div>
    </div>
    <div className="h-5 w-5 bg-gray-300 dark:bg-gray-700 rounded-full relative">
      <div className="absolute top-0 right-0 w-2 h-2 bg-gray-400 dark:bg-gray-600 rounded-full"></div>
    </div>
  </motion.div>
);

// Main SkeletonUserDashboard Component with typed props
const SkeletonUserDashboard: React.FC<{ activeTab: ActiveTab }> = ({ activeTab }) => {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-800 flex flex-col lg:flex-row text-gray-900 dark:text-white overflow-x-hidden">
      <SkeletonSidebar />
      <div className="flex-1 flex flex-col w-full">
        <SkeletonHeader />
        <main className="flex-1 py-8 sm:py-12 px-4 sm:px-6 lg:px-8 w-full overflow-x-auto">
          <div className="max-w-full sm:max-w-7xl mx-auto">
            {activeTab === "overview" && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
                <div className="flex justify-between items-center mb-8 sm:mb-12 flex-col gap-3 sm:flex-row">
                  <div className="h-8 w-40 sm:w-48 bg-gray-300 dark:bg-gray-700 rounded"></div>
                  <div className="h-10 w-full sm:w-40 bg-gray-300 dark:bg-gray-700 rounded-full"></div>
                </div>
                <SkeletonUserInfoCard />
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-6 mb-4 sm:mb-6 w-full">
                  {Array(5).fill(0).map((_, index) => (
                    <SkeletonStatCard key={index} />
                  ))}
                </div>
                <div className="space-y-4 sm:space-y-6 w-full">
                  <div className="h-6 w-32 sm:w-40 bg-gray-300 dark:bg-gray-700 rounded"></div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                    {Array(3).fill(0).map((_, index) => (
                      <SkeletonOrderCard key={index} />
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
            {activeTab === "orders" && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
                <div className="flex justify-between items-center mb-8 sm:mb-12 flex-col gap-3 sm:flex-row">
                  <div className="h-8 w-40 sm:w-48 bg-gray-300 dark:bg-gray-700 rounded"></div>
                  <div className="h-10 w-full sm:w-40 bg-gray-300 dark:bg-gray-700 rounded-full"></div>
                </div>
                <SkeletonFilterBar />
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 w-full">
                  {Array(3).fill(0).map((_, index) => (
                    <SkeletonOrderCard key={index} />
                  ))}
                </div>
              </motion.div>
            )}
            {activeTab === "addresses" && <SkeletonForm />}
            {activeTab === "settings" && <SkeletonForm />}
          </div>
        </main>
      </div>
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }
        .animate-pulse {
          animation: pulse 1.5s ease-in-out infinite;
        }
        html {
          scroll-behavior: smooth;
        }
        main {
          -webkit-overflow-scrolling: touch;
        }
        @media (max-width: 640px) {
          .max-w-full {
            padding-left: 0.5rem;
            padding-right: 0.5rem;
          }
        }
      `}</style>
    </div>
  );
};

export default SkeletonUserDashboard;